/**
 * Section steps list → chunk prefetch (Phase 4 W2a, see
 * Step/composables/useStepChunkPrefetch.ts). Prefetch only: no HTTP requests
 * to the API, no state.
 *
 * - When the list's set of distinct step kinds becomes known (or changes),
 *   schedules ONE idle prefetch for those kinds. Re-renders and repo updates
 *   that keep the same kinds do not prefetch again.
 * - `prefetchStepByDocId(docId)` warms one step's chunks now; call it on
 *   click, BEFORE the user-step POST, so the chunks download alongside it.
 */
import { computed, toValue, watch, type MaybeRefOrGetter } from 'vue';
import {
  prefetchKindOf,
  useStepChunkPrefetch,
  type PrefetchSourceStep,
} from 'src/modules/Step/composables/useStepChunkPrefetch';

type ListStep = PrefetchSourceStep & { documentId?: string | null };

export function useSectionStepsPrefetch(
  steps: MaybeRefOrGetter<ReadonlyArray<ListStep | null | undefined>>,
) {
  const { prefetchForSteps, prefetchForStep } = useStepChunkPrefetch();

  const kindsKey = computed(() => {
    const kinds = new Set<string>();
    for (const step of toValue(steps)) {
      const kind = prefetchKindOf(step);
      if (kind) kinds.add(kind);
    }
    return [...kinds].sort().join(',');
  });

  watch(
    kindsKey,
    (key) => {
      if (key) prefetchForSteps(toValue(steps));
    },
    { immediate: true },
  );

  function prefetchStepByDocId(docId: string | null | undefined) {
    if (!docId) return;
    prefetchForStep(toValue(steps).find((s) => s?.documentId === docId));
  }

  return { prefetchStepByDocId };
}
